import React from 'react';
import PropTypes from "prop-types";
import {pick} from "lodash";
import {Badge, ButtonGroup, Col, Form, FormFeedback, FormGroup, Input, Label} from "reactstrap";
import AppFormDefault from "componentsReusable/AppForm/AppFormDefault";
import ClientSelect from "../Client/ClientSelect";
import Link from "../Application/Link";
import appUserRoutes from "../../appUserRoutes";
import Editor from "../Editor";
import {withTranslation} from "react-i18next";
import Button from "../Button";
import Icon from "../../componentsReusable/Icon";
import {ATTACHMENT_TYPE_SALE_DOCUMENT, ATTACHMENT_TYPE_STORAGE_DOCUMENT} from "../../utils/ClientMail/Attachment";
import classnames from "classnames";
import Tooltip from "../../componentsReusable/Tooltip";
import getClientName from "../../utils/clientName";
import AppTableModal from "../../componentsReusable/AppTableModal";
import ClientsTable from "../Client/ClientsTable";
import EmailSelect from "../EmailSelect";

class ClientMailForm extends AppFormDefault {
  constructor(props) {
    super(props);

    const attachments = props.data.attachments
      .filter(a => a.type === ATTACHMENT_TYPE_STORAGE_DOCUMENT || a.type === ATTACHMENT_TYPE_SALE_DOCUMENT)
      .map(attachment => {
        let previewUrl, docNumber;

        if (attachment.type === ATTACHMENT_TYPE_STORAGE_DOCUMENT) {
          previewUrl = appUserRoutes.storageDocument.documentPreview(attachment.storageDocument.id);
          docNumber = attachment.storageDocument.number;
        } else if (attachment.type === ATTACHMENT_TYPE_SALE_DOCUMENT) {
          previewUrl = appUserRoutes.office.saleDocument.documentPreview(attachment.saleDocument.id);
          docNumber = attachment.saleDocument.number;
        }

        return {
          ...attachment,
          previewUrl,
          docNumber,
          selected: true,
        };
      });

    this.state = {
      data: {
        ...pick(props.data, [
          'id',
          'client',
          'emails',
          'subject',
          'content',
          'attachments',
        ]),
        attachments,
        clients: [],
      },
      clientSelectDisabled: this.props.data.clientSelectDisabled,
    };

  }

  handleEmailChange = (newEmails) => {
    this.setState(prevState => ({
      data: {
        ...prevState.data,
        emails: newEmails,
      }
    }));
  };

  toggleAttachment = (attachmentIndex) => {
    this.setState((prevState) => {
      const updatedAttachments = [...prevState.data.attachments];
      updatedAttachments[attachmentIndex].selected = !updatedAttachments[attachmentIndex].selected;
      return {
        data: {
          ...prevState.data,
          attachments: updatedAttachments,
        },
      };
    });
  }

  submit = () => {
    const { data } = this.state;
    const attachmentsToSend = data.attachments.filter(attachment => attachment.selected);

    this.props.onSubmit({
      ...data,
      attachments: attachmentsToSend,
    });
  };

  removeClientById = clientId => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        clients: prevState.data.clients.filter(client => client.id !== clientId)
      }
    }));
  }

  render() {
    const {data, clientSelectDisabled} = this.state;
    const {errors} = this.props;

    const invalidEmails = Object.keys(errors).filter((key) => key.startsWith("emails[")).map((key) => {
      const match = key.match(/emails\[(\d+)\]/);
      if (match) {
        const index = parseInt(match[1], 10);
        return data.emails[index];
      }
      return null;
    })
      .filter((email) => email != null);

    const clientEmailErrors = Object.keys(errors)
      .filter(key => key.startsWith("clients["))
      .map(key => {
        const match = key.match(/clients\[(\d+)\].email/);
        if (match) {
          const index = parseInt(match[1], 10);
          return index;
        }
        return null;
      })
      .filter(index => index != null);

    return (
      <Form onSubmit={this.submit}>
        <FormGroup row>
          <Label xs={3}>
            <span>{this.props.t('client')}</span>
            {data.clients.length > 0 && <span className="ml-2">({data.clients.length})</span>}
          </Label>
          <Col xs={9} className="d-flex flex-row flex-nowrap w-100">
            {
              data.clients.length > 0
                ? <div className="flex-grow-1 d-flex flex-row flex-wrap" style={{maxWidth: 'calc(100% - 42px)', maxHeight: '17vh', overflowY: 'auto'}}>{data.clients.map((client, index) => {
                  const hasEmailError = clientEmailErrors.includes(index);
                  return (<Badge color={'light'} className={`mr-1 mb-1 d-flex flex-row flex-nowrap align-items-center ${hasEmailError ? 'text-danger' : ''}`} style={{maxWidth: '32%'}}>
                    <Tooltip msg={client?.email ?? <i className="text-warning">{this.props.t('email_empty')}</i>}><div className="text-ellipsis"><span>{getClientName(client)}</span></div></Tooltip>
                    <Icon tooltip={this.props.t('email_remove_tooltip')} className="fas fa-times cursor-pointer ml-2 text-secondary" onClick={() => this.removeClientById(client.id)} />
                  </Badge>)
                })}</div>
                : <ClientSelect
                  className="flex-grow-1"
                  isDisabled={clientSelectDisabled}
                  client={data?.client?.id}
                  onChange={client => {
                    let obj = {
                      client,
                      emails: client?.email ? [client.email] : [],
                    };

                    this.onChangeData(obj);
                  }}
                />
            }
            {!clientSelectDisabled && <AppTableModal
              size={'w-100'}
              tableComponent={ClientsTable}
              clickToSelect
              onAccept={(clientsSelected) => {
                const newClients = clientsSelected
                  .filter((newClient) => !!newClient.email)
                  .filter((newClient) => !this.state.data.clients.some((existingClient) => existingClient.id === newClient.id));

                this.onChangeData({
                  clients: [...this.state.data.clients, ...newClients],
                  client: null,
                  emails: [],
                });
              }}
            >
              <Button type={'button'} color={"secondary"} className="ml-2 flex-grow-1" title={this.props.t('field.add_clients_button')} size="sm"><i className="fas fa-users"/></Button>
            </AppTableModal>}
          </Col>
        </FormGroup>
        {data.clients.length === 0 && <FormGroup row>
          <Label md={3}>{this.props.t('email')}</Label>

          <Col md="9">
            <EmailSelect onChange={this.handleEmailChange} isMulti={true} isCreateable={true} value={data.emails} invalidEmails={invalidEmails}/>
            {errors.emails && <FormFeedback className="d-block">{errors.emails}</FormFeedback>}
          </Col>
        </FormGroup>}
        <FormGroup row>
          <Label md={3}>{this.props.t('subject')}</Label>
          <Col md="9">
            <Input invalid={!!errors.subject} autoFocus placeholder={this.props.t('subject')} onChange={e => this.onChangeData({subject: e.target.value})} value={data.subject} />
            <FormFeedback>{errors.subject?.join('; ')}</FormFeedback>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md={3}>{this.props.t('content')}</Label>
          <Col md="9">
            <Editor
              onChange={content => this.onChangeData({ content })}
              value={data.content}
            />
            {errors.content && <FormFeedback className="d-block">{errors.content?.join('; ')}</FormFeedback>}
          </Col>
        </FormGroup>
        {data.attachments.length > 0 && <FormGroup row>
          <Label md={3}>{this.props.t('attachments')}</Label>
          <Col md="9">
            {data.attachments.map((attachment, index) => (
              <div key={index} className="mb-2">
                <ButtonGroup size={'sm'}>
                  <Button color={attachment.selected ? 'primary' : 'light'} onClick={(e) => {
                    e.preventDefault();
                    this.toggleAttachment(index);
                  }}>
                    {attachment.selected ? (<Icon className={"far fa-check-square mr-0"}/>) : (<Icon className={"far fa-square ml-0"}/>)}
                  </Button>
                  <Link className={classnames({
                    'btn btn-sm mr-2': true,
                    'btn-primary': attachment.selected,
                    'text-muted btn-light': !attachment.selected,
                  })} style={{
                    'text-decoration': !attachment.selected ? 'line-through' : 'none',
                  }} target={'_blank'} to={attachment.previewUrl}>{attachment.docNumber}</Link>
                </ButtonGroup>
              </div>
            ))}
          </Col>
        </FormGroup>}
      </Form>
    );
  }
}

ClientMailForm.defaultProps = {
  data: {
    client: null,
    emails: [],
    subject: null,
    content: null,
    attachments: [],
    clientSelectDisabled: false,
    clients: []
  },
};

ClientMailForm.propTypes = {
  errors: PropTypes.object.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    client: PropTypes.object,
    emails: PropTypes.array,
    subject: PropTypes.string,
    content: PropTypes.string,
    attachments: PropTypes.array,
    clientSelectDisabled: PropTypes.bool,
    clients: PropTypes.array
  }),
  onSubmit: PropTypes.func.isRequired,
};

export default withTranslation('client_mail_form', {withRef: true})(ClientMailForm);
